import moment from "moment";
import "moment-timezone";

const BAYANAT_CUTOFF = "2025-11-27T00:00:00.000+00:00";
const FLEET_CUTOFF = "2025-10-31T00:00:00.000+00:00";

const DEFAULT_TERMS_INVOICE = [
  {
    en: "Payment is due by the invoice due date shown above. Please quote the invoice number with your payment.",
    ar: "يستحق الدفع في تاريخ استحقاق الفاتورة الموضح أعلاه. يرجى ذكر رقم الفاتورة عند الدفع.",
  },
  {
    en: "Any discrepancy must be reported within 7 days of the invoice date.",
    ar: "يجب الإبلاغ عن أي اختلاف خلال 7 أيام من تاريخ الفاتورة.",
  },
  {
    en: "Bank charges, if any, are borne by the customer.",
    ar: "تتحمل الجهة العميلة أي رسوم بنكية إن وجدت.",
  },
];

const decodeName = (raw) => {
  if (!raw) return "";
  return String(raw)
    .replace(/:amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'");
};

const normalizeDateParam = (value) => {
  if (!value || typeof value !== "string") return value;
  return value
    .replace(/(\.\d{3})\s(\d{2}:\d{2})$/, "$1+$2")
    .replace(/T(\d{2}:\d{2}:\d{2})\s(\d{2}:\d{2})$/, "T$1+$2");
};

/** Manual invoice due dates often arrive as DD-MM-YYYY [HH:mm:ss] */
const LOCAL_DATE_FORMATS = [
  "DD-MM-YYYY HH:mm:ss",
  "DD-MM-YYYY HH:mm",
  "DD-MM-YYYY",
  "DD/MM/YYYY HH:mm:ss",
  "DD/MM/YYYY HH:mm",
  "DD/MM/YYYY",
];

const parseDocMoment = (value) => {
  const normalized = normalizeDateParam(value);
  if (normalized == null || normalized === "") return null;

  if (typeof normalized === "string") {
    for (const f of LOCAL_DATE_FORMATS) {
      const local = moment(normalized, f, true);
      if (local.isValid()) {
        return { m: local, calendarOnly: true };
      }
    }
  }

  const utc = moment.utc(normalized);
  if (utc.isValid()) return { m: utc, calendarOnly: false };
  return null;
};

/**
 * @param {string|Date} value
 * @param {string} timeZone
 * @param {boolean} withTime
 * @param {"dateOnly"|undefined} mode - dateOnly: keep calendar day (no TZ day-shift)
 */
export function formatDocDate(
  value,
  timeZone = "Asia/Riyadh",
  withTime = true,
  mode,
) {
  if (!value) return "";

  const dateOnly = mode === "dateOnly" || withTime === false;
  const fmt = dateOnly ? "DD MMM YYYY" : "DD MMM YYYY HH:mm";

  try {
    const parsed = parseDocMoment(value);
    if (!parsed) return String(value);

    const { m, calendarOnly } = parsed;
    // DD-MM-YYYY style / dateOnly: show the same calendar day (e.g. 28 Oct 2026)
    if (calendarOnly || mode === "dateOnly") {
      return m.format(fmt);
    }

    return m.tz(timeZone || "Asia/Riyadh").format(fmt);
  } catch (e) {
    return String(value);
  }
}

function resolveCompanyBrand(company, brandDate) {
  if (company?.companyName) {
    return {
      name: company.companyName,
      nameAr: company.companyNameArb || "",
      lines: [
      company.addressEng1,
      company.addressEng2,
      company.vatNumber ? `VAT: ${company.vatNumber}` : "",
      company.phone ? `Phone: ${company.phone}` : "",
      company.email || "",
      ].filter(Boolean),
      linesAr: [
      company.addressArb1,
      company.addressArb2,
      company.vatNumber ? `الرقم الضريبي: ${company.vatNumber}` : "",
      company.phone ? `الهاتف: ${company.phone}` : "",
      company.email || "",
      ].filter(Boolean),
      logo: "logo.png",
    };
  }

  const d = brandDate || "";
  if (d > BAYANAT_CUTOFF) {
    return {
      name: "Bayanat Smart Systems LLC",
      nameAr: "بيانات للأنظمة الذكية ذ م م",
      lines: [],
      linesAr: [],
      logo: "logo.png",
    };
  }
  if (d > FLEET_CUTOFF) {
    return {
      name: "National Fleet Car Rental Company",
      nameAr: "شركة الاسطول الوطنية لتأجير السيارات",
      lines: [],
      linesAr: [],
      logo: "logo.png",
    };
  }
  return {
    name: "National Transportation Solutions Company",
    nameAr: "شركة الوطنية لحلول النقل المحدودة",
    lines: [],
    linesAr: [],
    logo: "logo.png",
  };
}

function buildBank(setting) {
  const a = setting?.attributes || {};
  const rows = [
    ["Bank name", "اسم البنك", a.bankNameEnglish || a.bankName || null],
    [
      "Account holder",
      "اسم الحساب",
      a.bankHolderEnglish || a.bankHolder || null,
    ],
    [
      "Account no.",
      "رقم الحساب",
      a.accountNumberEnglish || a.accountNumber || null,
    ],
    ["IBAN", "رقم الآيبان", a.ibanEnglish || a.iban || null],
  ];
  if (!rows.some((r) => r[2])) return null;
  return { rows };
}

function buildTermsFromSetting(setting) {
  const a = setting?.attributes || {};
  const en = a.termsEnglishMaintenance;
  const ar = a.termsArabicMaintenance;
  if (!en && !ar) return DEFAULT_TERMS_INVOICE;
  if (typeof en === "string" && en.includes("\n")) {
    const enLines = en.split(/\n+/).map((s) => s.trim()).filter(Boolean);
    const arLines = (ar || "")
      .split(/\n+/)
      .map((s) => s.trim())
      .filter(Boolean);
    return enLines.map((line, i) => ({ en: line, ar: arLines[i] || "" }));
  }
  return [{ en: en || "", ar: ar || "" }];
}

function getItemSavings(item) {
  if (!(item?.marketPrice > 0)) return 0;
  return Math.max(
    0,
    (item.marketPrice - (item.saleNetAmount || 0)) * (item.itemQty ?? 1),
  );
}

/** Prefer categoryName (packages / services / items / servicepkgs) — DN + manual. */
function itemCategoryRaw(item = {}) {
  const raw =
    item.categoryName ||
    item.category ||
    item.itemCategory ||
    item.expenseType ||
    item.attributes?.categoryName ||
    item.attributes?.category ||
    item.attributes?.expenseType ||
    item.itemType ||
    item.attributes?.itemType ||
    "";
  if (raw && typeof raw === "object") {
    return raw.label || raw.name || raw.value || raw.id || "";
  }
  return raw;
}

/**
 * Map categoryName → package | service | item
 * Matches legacy: packages | services | items; also servicepkgs (manual invoice).
 */
function itemKind(item) {
  const t = String(itemCategoryRaw(item)).toLowerCase().trim();
  if (!t) return "item";
  // servicepkgs / pkgs before "service" so they don't land in services
  if (
    t === "packages" ||
    t === "package" ||
    t === "servicepkgs" ||
    t === "servicepkg" ||
    t.includes("package") ||
    t.includes("servicepkg") ||
    /(^|[^a-z])pkgs?([^a-z]|$)/.test(t)
  ) {
    return "package";
  }
  if (t === "services" || t === "service" || t.includes("service")) {
    return "service";
  }
  // "items" / "item" / anything else under parts → items group
  return "item";
}

/** Flatten packages / services / items arrays from DN/invoice payloads. */
function collectLineItems(data = {}) {
  const out = [];
  const seen = new Set();
  const pushAll = (arr, forcedCategory) => {
    (arr || []).forEach((it, idx) => {
      if (!it || typeof it !== "object") return;
      const key =
        it.id != null
          ? `id:${it.id}`
          : `${forcedCategory || ""}:${it.itemName || ""}:${it.itemCode || ""}:${idx}`;
      if (seen.has(key)) return;
      seen.add(key);
      if (forcedCategory && !itemCategoryRaw(it)) {
        out.push({ ...it, categoryName: forcedCategory });
      } else {
        out.push(it);
      }
    });
  };

  if (Array.isArray(data.items) && data.items.length) {
    pushAll(data.items);
    const kinds = new Set(data.items.map((it) => itemKind(it)));
    if (!kinds.has("package")) pushAll(data.packages, "packages");
    if (!kinds.has("service")) pushAll(data.services, "services");
  } else {
    pushAll(data.packages, "packages");
    pushAll(data.services, "services");
    pushAll(data.parts || data.spareParts, "items");
  }
  return out;
}

/**
 * Group DN/invoice lines by categoryName:
 * packages → services → items (legacy order + titles).
 */
function groupLineItems(items = [], { alwaysTitle = false } = {}) {
  const buckets = {
    package: {
      en: "Particulars of packages",
      ar: "تفاصيل الباقات",
      rows: [],
      alwaysTitle,
    },
    service: {
      en: "Particulars of services",
      ar: "تفاصيل الخدمات",
      rows: [],
      alwaysTitle,
    },
    item: {
      en: "Particulars of items",
      ar: "تفاصيل قطع الغيار",
      rows: [],
      alwaysTitle,
    },
  };

  items.forEach((item) => {
    const kind = itemKind(item);
    const qty = item.itemQty ?? item.qty ?? item.quantity ?? 1;
    const unitPrice =
      item.saleItemPrice != null
        ? +item.saleItemPrice
        : item.saleCost != null
          ? +item.saleCost
          : item.price != null
            ? +item.price
            : qty
              ? +(item.saleTotalAmount || item.saleNet || 0) / (qty || 1)
          : +item.saleTotalAmount || 0;
    const row = {
      name:
        item.itemName ||
        item.itemDescription ||
        item.description ||
        item.name ||
        "",
      sub:
        item.itemCode ||
        item.sku ||
        item.sub ||
        item.itemDescription ||
        "",
      qty,
      price: unitPrice,
      vat:
        item.saleVATAmount != null
          ? +item.saleVATAmount
          : item.saleVat != null
            ? +item.saleVat
            : item.vat != null
              ? +item.vat
              : undefined,
      saving: getItemSavings(item),
      discount: +item.discount || +item.saleDiscount || 0,
      rate: item.vatPercentage,
    };

    const bucket = buckets[kind] || buckets.item;
    bucket.rows.push(row);
  });

  return ["package", "service", "item"]
    .map((k) => buckets[k])
    .filter((g) => g.rows.length)
    .map((g) => (alwaysTitle ? { ...g, alwaysTitle: true } : g));
}

function customerBox(attrs = {}, { singlePo = false } = {}) {
  const name = decodeName(attrs.c_accountName);
  const addressParts = [
    attrs.c_Location,
    attrs.c_buildingNo,
    attrs.c_address,
    attrs.c_streetName,
  ]
    .filter(Boolean)
    .join(", ");
  const cityEn = [attrs.c_city, attrs.c_country].filter(Boolean).join(", ");
  const cityAr = [attrs.c_cityArabic, attrs.c_countryArabic]
    .filter(Boolean)
    .join("، ");

  const contact =
    attrs.c_contactName ||
    attrs.c_contact ||
    attrs.contactName ||
    attrs.c_customerName ||
    "";
  const phone = attrs.c_customerNo || attrs.c_phone || "";

  return {
    en: "Customer details",
    ar: "بيانات العميل",
    name,
    nameAr: attrs.c_nameArabic || "",
    rows: [
      singlePo
        ? ["Contact", "", contact || phone]
        : ["Phone", "", phone],
      ["Email", "", attrs.c_email || ""],
      ["TRN", "", attrs.c_taxNumber || attrs.c_VATNumber || ""],
      [
        "Address",
        "",
        addressParts || attrs.c_address || "",
        attrs.c_addressArabic || attrs.c_LocationArabic || "",
      ],
      ["City", "", cityEn, cityAr],
      ["Postal code", "", attrs.c_postalCode || ""],
    ],
  };
}

function vehicleBox(data = {}) {
  const attrs = data.attributes || {};
  const plate = [data.plateNumber, attrs.v_cityShortCode]
    .filter((v) => v && v !== "---")
    .join(" · ");
  const make = attrs.make || attrs.v_make || data.vehicleName || data.make || "";
  const model = attrs.model || attrs.v_model || data.model || "";
  const vehicle =
    data.vehicleName ||
    [make, model].filter((v) => v && v !== "---").join(" ") ||
    "";
  const vin =
    attrs.vin || attrs.v_vin || data.vin || data.chassisNumber || "";
  const mileage =
    data.mileage ?? data.odometerReading ?? attrs.odometerReading;

  return {
    en: "Vehicle details",
    ar: "بيانات المركبة",
    rows: [
      ["Plate", "", plate],
      ["Make / Model", "", vehicle],
      ["VIN", "", vin && vin !== "---" ? vin : ""],
      [
        "Mileage",
        "",
        mileage != null && mileage !== ""
          ? `${Number(mileage).toLocaleString("en-US")} km`
          : "",
      ],
    ],
  };
}

function printedNow(timezone) {
  return formatDocDate(new Date().toISOString(), timezone, true);
}

/** Delivery note or single-PO tax invoice DOC */
export function mapDeliveryNoteToDoc(
  data = {},
  company,
  { timezone = "Asia/Riyadh", singlePo = false, inv, sDate } = {},
) {
  const attrs = data.attributes || {};
  const brandDate =
    data.serviceDate || data.deliveryNoteDate || data.createdDate;
  const brand = resolveCompanyBrand(company, brandDate);
  const items = collectLineItems(data);
  const groups = groupLineItems(items, { alwaysTitle: true });
  const showSavings = groups.some((g) => g.rows.some((r) => r.saving > 0));
  const credit = attrs.c_creditPeriod;
  const workshop = [
    data.workshopName || data.stationName,
    data.areaName || data.stationCity,
  ]
    .filter(Boolean)
    .join(" · ");

  const badges = singlePo
    ? [{ en: "Single PO invoice", ar: "فاتورة أمر عمل" }]
    : [{ en: "Valid" }];

  const refs = singlePo
    ? [
        [
          "Invoice No",
          "رقم الفاتورة",
          inv || data.invoiceNumber || data.deliveryNoteNumber || "",
        ],
        [
          "Invoice date",
          "تاريخ الفاتورة",
          formatDocDate(
            sDate || data.serviceDate || data.deliveryNoteDate,
            timezone,
          ),
        ],
        [
          "Due date",
          "تاريخ الاستحقاق",
          formatDocDate(
            data.dueDate || sDate || data.serviceDate,
            timezone,
            false,
          ),
        ],
        ["Work order #", "رقم أمر العمل", data.workOrderNumber || ""],
        [
          "Delivery note ref",
          "مرجع الإشعار",
          data.deliveryNoteRefNumber || data.invoiceRefNumber || "",
        ],
        [
          "Contract No",
          "رقم العقد",
          (data.contractNumber || attrs.c_contractNumber || "")
            .toString()
            .toUpperCase(),
        ],
      ]
    : [
        ["Delivery note No", "رقم الإشعار", data.deliveryNoteNumber || ""],
        [
          "Delivery note date",
          "تاريخ الإشعار",
          formatDocDate(
            data.deliveryNoteDate || data.serviceDate,
            timezone,
          ),
        ],
        [
          "Service date",
          "تاريخ الخدمة",
          formatDocDate(
            data.serviceRefDate || data.serviceDate,
            timezone,
          ),
        ],
        ["Work order #", "رقم أمر العمل", data.workOrderNumber || ""],
        [
          "Delivery note ref",
          "مرجع الإشعار",
          data.deliveryNoteRefNumber || "",
        ],
        [
          "Booking / Ref #",
          "رقم الحجز",
          data.bookingId ||
            (data.serviceId
              ? `${data.serviceId}${
                  data.serviceRefNumber ? " / " + data.serviceRefNumber : ""
                }`
              : data.serviceRefNumber || ""),
        ],
        [
          "Contract No",
          "رقم العقد",
          (data.contractNumber || attrs.c_contractNumber || "")
            .toString()
            .toUpperCase(),
        ],
        [
          "Credit period",
          "فترة الائتمان",
          credit != null && credit !== "" ? `${credit} days` : "",
        ],
        ["Workshop", "الورشة", workshop],
      ];

  return {
    titleEn: singlePo ? "TAX INVOICE" : "DELIVERY NOTE",
    titleAr: singlePo ? "فاتورة ضريبية" : "إشعار إنجاز",
    docWord: singlePo ? "invoice" : "service",
    logo: brand.logo,
    badges,
    company: {
      name: brand.name,
      nameAr: brand.nameAr,
      lines: brand.lines.length
        ? brand.lines
        : [
            company?.addressEng1,
            company?.addressEng2,
            company?.vatNumber ? `VAT: ${company.vatNumber}` : "",
            company?.phone ? `Phone: ${company.phone}` : "",
            company?.email,
          ].filter(Boolean),
      linesAr: brand.linesAr.length
        ? brand.linesAr
        : [
            company?.addressArb1,
            company?.addressArb2,
            company?.vatNumber ? `الرقم الضريبي: ${company.vatNumber}` : "",
            company?.phone ? `الهاتف: ${company.phone}` : "",
            company?.email,
          ].filter(Boolean),
    },
    notice: singlePo
      ? null
      : {
          en: "This document is a delivery note — not a tax invoice",
          ar: "هذه الوثيقة إشعار إنجاز وليست فاتورة ضريبية",
        },
    refs,
    boxes: [customerBox(attrs, { singlePo }), vehicleBox(data)],
    table: { mode: "items", groups },
    showSavings,
    vatRate: data.vatPercentage ?? 5,
    bank: singlePo ? buildBank(data.setting) : null,
    terms: singlePo ? buildTermsFromSetting(data.setting) : [],
    footer: {
      en: "This is a system generated document and needs no signatures.",
      ar: "مستند تم إنشاؤه بواسطة النظام ولا يتطلب توقيعاً.",
    },
    printed: printedNow(timezone),
  };
}

/** Monthly / consolidated maintenance tax invoice DOC */
export function mapMonthlyInvoiceToDoc(
  data = {},
  company,
  { timezone = "Asia/Riyadh" } = {},
) {
  const attrs = data.attributes || {};
  const brandDate = data.serviceDate || data.invoiceDate || data.createdDate;
  const brand = resolveCompanyBrand(company, brandDate);
  const rawItems = data.items || [];
  const looksLikeDn = rawItems.some(
    (it) => it.deliveryNoteNumber || it.dnNumber || it.dn,
  );

  let groups;
  let mode;
  if (looksLikeDn) {
    mode = "dn";
    groups = [
      {
        en: "Delivery notes",
        ar: "مذكرات التسليم",
        alwaysTitle: true,
        rows: rawItems.map((it) => {
          const a = it.attributes || {};
          const make =
            a.v_make || a.make || it.make || it.vehicleMake || "";
          const model =
            a.v_model || a.model || it.model || it.vehicleModel || "";
          const year = a.v_makeYear || a.makeYear || it.makeYear || "";
          const vehicle =
            it.vehicleName ||
            [make, model, year].filter((v) => v && v !== "---").join(" ") ||
            "";
          const vin =
            a.v_vin ||
            a.vin ||
            it.vin ||
            it.chassisNumber ||
            "";
          return {
            plate: it.plateNumber || it.plate || a.plateNumber || "",
            vehicle,
            vin: vin && vin !== "---" ? vin : "",
          dn: it.deliveryNoteNumber || it.dnNumber || it.dn || "",
            date: formatDocDate(
              it.serviceDate || it.deliveryNoteDate,
              timezone,
              false,
            ),
            qty: 1,
            price:
              +it.saleItemPrice ||
              +it.saleNetAmount ||
              +it.saleTotalAmount ||
              0,
          vat: it.saleVATAmount != null ? +it.saleVATAmount : undefined,
          saving: getItemSavings(it),
            discount: +it.discount || +it.saleDiscount || 0,
          };
        }),
      },
    ];
  } else {
    mode = "items";
    // Manual / line invoices: group by categoryName (packages · services · items)
    groups = groupLineItems(collectLineItems(data), { alwaysTitle: true });
  }

  const dnCount =
    data.deliveryNoteCount ||
    data.totalDeliveryNotes ||
    data.attributes?.deliveryNoteCount ||
    (looksLikeDn ? rawItems.length : null);
  const period =
    data.periodLabel ||
    [
      formatDocDate(data.periodFrom || data.fromDate, timezone, false),
      formatDocDate(data.periodTo || data.toDate, timezone, false),
    ]
      .filter(Boolean)
      .join(" – ");

  const credit = attrs.c_creditPeriod;
  const uniqueVehicles = new Set(
    rawItems
      .map((it) => {
        const a = it.attributes || {};
        return (
          it.plateNumber ||
          it.plate ||
          a.v_vin ||
          a.vin ||
          it.vin ||
          it.chassisNumber ||
          ""
        );
      })
      .filter(Boolean),
  );

  return {
    titleEn: "TAX INVOICE",
    titleAr: "فاتورة ضريبية",
    docWord: "invoice",
    logo: brand.logo,
    badges: [{ en: "Monthly invoice", ar: "فاتورة شهرية" }],
    company: {
      name: brand.name,
      nameAr: brand.nameAr,
      lines: brand.lines.length
        ? brand.lines
        : [
            company?.addressEng1,
            company?.addressEng2,
            company?.vatNumber ? `VAT: ${company.vatNumber}` : "",
            company?.phone ? `Phone: ${company.phone}` : "",
            company?.email,
          ].filter(Boolean),
      linesAr: brand.linesAr.length
        ? brand.linesAr
        : [
            company?.addressArb1,
            company?.addressArb2,
            company?.vatNumber ? `الرقم الضريبي: ${company.vatNumber}` : "",
            company?.phone ? `الهاتف: ${company.phone}` : "",
            company?.email,
          ].filter(Boolean),
    },
    notice: {
      en:
        data.noticeEn ||
        (dnCount
          ? `Consolidated invoice for ${dnCount} delivery notes${
              period ? ` · ${period}` : ""
            }`
          : "Consolidated monthly invoice"),
      ar:
        data.noticeAr ||
        (dnCount
          ? `فاتورة مجمّعة لـ ${dnCount} مذكرة تسليم`
          : "فاتورة شهرية مجمّعة"),
    },
    refs: [
      [
        "Invoice No",
        "رقم الفاتورة",
        data.invoiceNumber || data.monthlyInvoiceNumber || "",
      ],
      [
        "Service date",
        "تاريخ الخدمة",
        formatDocDate(data.serviceDate || data.invoiceDate, timezone, false),
      ],
      ["Period", "الفترة", period],
      [
        "Due date",
        "تاريخ الاستحقاق",
        formatDocDate(
          data.attributes?.invoiceDueDate ||
            data.invoiceDueDate ||
            data.paymentDueDate,
          timezone,
          false,
          "dateOnly",
        ),
      ],
      [
        "Credit period",
        "فترة الائتمان",
        credit != null && credit !== "" ? `${credit} days` : "",
      ],
      [
        "Delivery notes",
        "مذكرات التسليم",
        dnCount != null ? String(dnCount) : "",
      ],
      [
        "Booking / Ref #",
        "رقم الحجز / المرجع",
        data.bookingId || data.serviceRefNumber || "",
      ],
              [
            "Contract No",
            "رقم العقد",
            (data.contractNumber || attrs.c_contractNumber || "")
              .toString()
              .toUpperCase(),
          ],
    ],
    boxes: [
      customerBox(attrs),
      {
        en: "Contract details",
        ar: "بيانات العقد",
        name: decodeName(attrs.c_contractName || attrs.c_accountName),
        rows: [
          [
            "Contract No",
            "",
            (data.contractNumber || attrs.c_contractNumber || "")
              .toString()
              .toUpperCase(),
          ],
          ["Contract period", "", period],
          [
            "Credit period",
            "",
            credit != null && credit !== "" ? `${credit} days` : "",
          ],
          [
            "Vehicles serviced",
            "",
            data.vehicleCount ||
              data.vehiclesServiced ||
              (uniqueVehicles.size ? String(uniqueVehicles.size) : ""),
          ],
          ["Delivery notes", "", data?.stats],
        ],
      },
    ],
    table: { mode, groups },
    showSavings: groups.some((g) => g.rows.some((r) => (r.saving || 0) > 0)),
    vatRate: data.vatPercentage ?? 5,
    bank: buildBank(data.setting),
    terms: buildTermsFromSetting(data.setting),
    footer: {
      en: "This is a system generated document and needs no signatures.",
      ar: "مستند تم إنشاؤه بواسطة النظام ولا يتطلب توقيعاً.",
    },
    printed: printedNow(timezone),
  };
}

/** Lease tax invoice DOC */
export function mapLeaseInvoiceToDoc(
  data = {},
  company,
  { timezone = "Asia/Riyadh" } = {},
) {
  const attrs = data.attributes || {};
  const brandDate = data.invoiceDate || data.createdDate || data.serviceDate;
  const brand = resolveCompanyBrand(company, brandDate);
  const items = data.items || [];
  const credit = attrs.c_creditPeriod;

  const cityCounts = {};
  let contractual = 0;
  let exclusive = 0;
  const itemCityCode = (it = {}) => {
    const a = it.attributes || {};
    return (
      a.v_cityShortCode ||
      a.cityShortCode ||
      it.cityShortCode ||
      it.cityCode ||
      a.v_cityCode ||
      it.city ||
      it.areaName ||
      ""
    );
  };
  items.forEach((it) => {
    const city = String(itemCityCode(it) || "").trim().toUpperCase();
    if (city) cityCounts[city] = (cityCounts[city] || 0) + 1;
    const pool = String(it.poolType || it.leaseType || "").toLowerCase();
    if (pool.includes("exclu")) exclusive += 1;
    else contractual += 1;
  });
  // e.g. AUH 4 · SHJ 2 · FUJ 1
  const citiesLabel = Object.entries(cityCounts)
    .map(([k, v]) => `${k} ${v}`)
    .join(" · ");
  const poolLabel =
    contractual || exclusive
      ? `Contractual ${contractual} · Exclusive ${exclusive}`
      : "";

  const billingDays =
    data.billingDays || data.noOfDays || items[0]?.noOfDays || "";

  const groups = [
    {
      en: "Leased vehicles",
      ar: "المركبات المؤجرة",
      alwaysTitle: true,
      rows: items.map((it) => ({
        plate: it.plateNumber || it.plate || "",
        plateAr: it.plateNumberArabic || it.plateAr || "",
        vehicle:
          it.model ||
          it.vehicleName ||
          [it.make, it.model].filter(Boolean).join(" ") ||
          "",
        name:
          it.itemDescription ||
          it.itemName ||
          "Monthly fleet lease · إيجار أسطول شهري",
        sub: [itemCityCode(it), it.poolType || it.leaseType]
          .filter(Boolean)
          .join(" · "),
        vin: it.vin || it.chassisNumber || "",
        days: it.noOfDays ?? billingDays ?? "",
        qty: it.itemQty ?? 1,
        price: +it.saleItemPrice || 0,
        vat: it.saleVATAmount != null ? +it.saleVATAmount : undefined,
        rate: it.vatPercentage,
        discount: +it.discount || +it.saleDiscount || 0,
      })),
    },
  ];

  return {
    titleEn: "TAX INVOICE",
    titleAr: "فاتورة ضريبية",
    docWord: "invoice",
    vatRate: data.vatPercentage ?? 5,
    logo: brand.logo,
    badges: [{ en: "Lease invoice", ar: "فاتورة تأجير" }],
    company: {
      name: brand.name,
      nameAr: brand.nameAr,
      lines: brand.lines.length
        ? brand.lines
        : [
            company?.addressEng1,
            company?.addressEng2,
            company?.vatNumber ? `VAT: ${company.vatNumber}` : "",
            company?.phone ? `Phone: ${company.phone}` : "",
            company?.email,
          ].filter(Boolean),
      linesAr: brand.linesAr.length
        ? brand.linesAr
        : [
            company?.addressArb1,
            company?.addressArb2,
            company?.vatNumber ? `الرقم الضريبي: ${company.vatNumber}` : "",
            company?.phone ? `الهاتف: ${company.phone}` : "",
            company?.email,
          ].filter(Boolean),
    },
    notice: {
      en:
        data.noticeEn ||
        `Monthly fleet lease · ${items.length} vehicles${
          data.periodLabel ? ` · ${data.periodLabel}` : ""
        }`,
      ar: data.noticeAr || `إيجار أسطول شهري · ${items.length} مركبات`,
    },
    refs: [
      ["Invoice No", "رقم الفاتورة", data.invoiceNumber || ""],
      [
        "Invoice date",
        "تاريخ الفاتورة",
        formatDocDate(data.invoiceDate || data.serviceDate, timezone, false),
      ],
      [
        "Billing period",
        "فترة الفوترة",
        data.billingPeriodLabel ||
          (billingDays
            ? `${billingDays} days${
                data.periodLabel ? ` · ${data.periodLabel}` : ""
              }`
            : data.periodLabel || ""),
      ],
      [
        "Contract No",
        "رقم العقد",
        (data.contractNumber || attrs.c_contractNumber || "")
          .toString()
          .toUpperCase(),
      ],
      [
        "Credit period",
        "فترة الائتمان",
        credit != null && credit !== "" ? `${credit} days` : "",
      ],
      [
        "Vehicles",
        "عدد المركبات",
        String(items.length || data.vehicleCount || ""),
      ],
    ],
    boxes: [
      customerBox(attrs),
      {
        en: "Lease summary",
        ar: "ملخص التأجير",
        rows: [
          [
            "Contract No",
            "",
            (data.contractNumber || attrs.c_contractNumber || "")
              .toString()
              .toUpperCase(),
          ],
          ["Vehicles", "", String(items.length || "")],
          ["Billing days", "", billingDays !== "" ? String(billingDays) : ""],
          ["Pool", "", poolLabel],
          ["Cities", "", citiesLabel],
        ],
      },
    ],
    table: { mode: "lease", groups },
    showSavings: false,
    paid: (data?.saleNetAmount ?? 0) - (data?.invoiceBalance ?? 0) || 0,
    invoiceBalance:data?.invoiceBalance,
    bank: buildBank(data.setting),
    terms: (() => {
      const a = data.setting?.attributes || {};
      // Prefer leasing terms; fall back to maintenance (account /terms API)
      const en = a.termsEnglishLeasing || a.termsEnglishMaintenance;
      const ar = a.termsArabicLeasing || a.termsArabicMaintenance;
      if (!en && !ar) {
        return [
          {
            en: "This is a system generated invoice and needs no signatures.",
            ar: "فاتورة تم إنشاؤها بواسطة النظام ولا تتطلب توقيعاً.",
          },
        ];
      }
      if (typeof en === "string" && en.includes("\n")) {
        const enLines = en.split(/\n+/).map((s) => s.trim()).filter(Boolean);
        const arLines = (ar || "")
          .split(/\n+/)
          .map((s) => s.trim())
          .filter(Boolean);
        return enLines.map((line, i) => ({ en: line, ar: arLines[i] || "" }));
      }
      return [{ en: en || "", ar: ar || "" }];
    })(),
    footer: { en: "", ar: "" },
    printed: printedNow(timezone),
  };
}

/** Other Customers tax invoice DOC (cashinvoices) */
export function mapCashCustomerInvoiceToDoc(
  data = {},
  company,
  { timezone = "Asia/Riyadh" } = {},
) {
  const attrs = data.attributes || {};
  const brandDate = data.invoiceDate || data.createdDate || data.serviceDate;
  const brand = resolveCompanyBrand(company, brandDate);
  const vatAmt = +data.saleVatAmount || +data.saleVATAmount || 0;
  const netAmt = +data.saleNetAmount || +data.saleItemPrice || 0;
  const totalAmt =
    +data.saleTotalAmount || netAmt + vatAmt || +data.saleNetAmount || 0;
  const vatRate =
    data.vatPercentage != null
      ? +data.vatPercentage
      : vatAmt > 0 && netAmt > 0
        ? Math.round((vatAmt / netAmt) * 100)
        : 0;

  const items = collectLineItems(data);
      const chargeType =
      data.chargeType ||
      attrs.chargeType ||
      (data.mrc ? "Recurring Charges" : "One-time charge");
  let groups;
  if (items.length) {
    groups = groupLineItems(items, { alwaysTitle: true });
  } else {
    const name =
      data.refNum ||
      attrs.refNum ||
      data.itemName ||
      data.description ||
      "Charge";

    const contractLabel = (
      data.contractNumber ||
      attrs.c_contractNumber ||
      attrs.c_accountName ||
      ""
    )
      .toString()
      .toUpperCase();
    groups = [
      {
        en: "Particulars of services",
        ar: "تفاصيل الخدمات",
        alwaysTitle: true,
        rows: [
          {
            name: decodeName(String(name)),
            sub: [chargeType, contractLabel].filter(Boolean).join(" · "),
            qty: 1,
            price: netAmt || totalAmt,
            vat: vatAmt,
            rate: vatRate,
            saving: 0,
            discount: 0,
          },
        ],
      },
    ];
  }

  const contractNo = (
    data.contractNumber ||
    attrs.c_contractNumber ||
    ""
  )
    .toString()
    .toUpperCase();

  return {
    titleEn: "TAX INVOICE",
    titleAr: "فاتورة ضريبية",
    docWord: "invoice",
    vatRate,
    logo: brand.logo,
    badges: [{ en: "Other Customers", ar: "عملاء آخرون" }],
    company: {
      name: brand.name,
      nameAr: brand.nameAr,
      lines: brand.lines.length
        ? brand.lines
        : [
            company?.addressEng1,
            company?.addressEng2,
            company?.vatNumber ? `VAT: ${company.vatNumber}` : "",
            company?.phone ? `Phone: ${company.phone}` : "",
            company?.email,
          ].filter(Boolean),
      linesAr: brand.linesAr.length
        ? brand.linesAr
        : [
            company?.addressArb1,
            company?.addressArb2,
            company?.vatNumber ? `الرقم الضريبي: ${company.vatNumber}` : "",
            company?.phone ? `الهاتف: ${company.phone}` : "",
            company?.email,
          ].filter(Boolean),
    },
    notice:
      vatRate === 0 || vatAmt === 0
        ? {
            en: "No VAT charged on this invoice",
            ar: "لا تُفرض ضريبة القيمة المضافة على هذه الفاتورة",
          }
        : null,
    refs: [
      [
        "Invoice No",
        "رقم الفاتورة",
        data.invoiceNumber ||
          (data.id != null ? `#${data.id}` : "") ||
          "",
      ],
      [
        "Invoice date",
        "تاريخ الفاتورة",
        formatDocDate(data.invoiceDate || data.serviceDate, timezone, false),
      ],
      [
        "Reference #",
        "الرقم المرجعي",
        decodeName(String(data.refNum || attrs.refNum || "")),
      ],
      ["Contract", "العقد", contractNo],
      [
        "Charge type",
        "نوع الرسوم",
       chargeType,
      ],
      [
        "Recorded on",
        "تاريخ التسجيل",
        formatDocDate(
          data.createdDate || data.recordedOn || data.invoiceDate,
          timezone,
          false,
        ),
      ],
    ],
    boxes: [
      {
        en: "Customer details",
        ar: "بيانات العميل",
        name:
          decodeName(attrs.c_accountName) ||
          contractNo ||
          decodeName(attrs.c_nameArabic) ||
          "",
        nameAr: attrs.c_nameArabic || "",
        rows: [
          ["Account type", "", "Other Customers"],
          ["Phone", "", attrs.c_customerNo || attrs.c_phone || ""],
          ["Email", "", attrs.c_email || ""],
          ["TRN", "", attrs.c_taxNumber || attrs.c_VATNumber || ""],
          [
            "Address",
            "",
            attrs.c_Location || attrs.c_address || "",
            attrs.c_addressArabic || "",
          ],
        ],
      },
      {
        en: "Invoice details",
        ar: "تفاصيل الفاتورة",
        rows: [
          ["Contract", "", contractNo],
          [
            "Reference",
            "",
            decodeName(String(data.refNum || attrs.refNum || "")),
          ],
          [
            "Charge type",
            "",
            chargeType,
          ],
          [
            "Last updated",
            "",
            formatDocDate(
              data.lastUpdate || data.updatedDate || data.invoiceDate,
              timezone,
              false,
            ),
          ],
        ],
      },
    ],
    table: { mode: "items", groups },
    showSavings: false,
    bank: buildBank(data.setting),
    terms: buildTermsFromSetting(data.setting),
    footer: {
      en: "This is a system generated document and needs no signatures.",
      ar: "مستند تم إنشاؤه بواسطة النظام ولا يتطلب توقيعاً.",
    },
    printed: printedNow(timezone),
  };
}

/** creationMode === manualInvoice | mannualInvoice */
export function isManualInvoiceCreation(data = {}) {
  const raw = String(
    data.creationMode ||
      data.attributes?.creationMode ||
      data.attributes?.mannualInvoice ||
      "",
  )
    .toLowerCase()
    .replace(/\s+/g, "");
  return (
    raw === "manualinvoice" ||
    raw === "mannualinvoice" ||
    raw === "true" // attributes.mannualInvoice flag
  );
}

/**
 * Manual invoice DOC (creationMode=manualInvoice) — categoryName groups.
 * Separate from monthly / Other Customers mappers.
 */
export function mapManualInvoiceToDoc(
  data = {},
  company,
  { timezone = "Asia/Riyadh" } = {},
) {
  const attrs = data.attributes || {};
  const brandDate =
    data.invoiceDate || data.serviceDate || data.createdDate || data.refDate;
  const brand = resolveCompanyBrand(company, brandDate);
  const vatAmt =
    +data.saleVatAmount || +data.saleVATAmount || +data.saleVat || 0;
  const netAmt =
    +data.saleNetAmount || +data.saleItemPrice || +data.saleNet || 0;
  const totalAmt =
    +data.saleTotalAmount ||
    +data.saleTotal ||
    netAmt + vatAmt ||
    0;
  const vatRate =
    data.vatPercentage != null
      ? +data.vatPercentage
      : attrs.zeroVat || attrs.zerovat
        ? 0
        : vatAmt > 0 && netAmt > 0
          ? Math.round((vatAmt / netAmt) * 100)
          : 5;

  const items = collectLineItems(data);
  let groups;
  if (items.length) {
    groups = groupLineItems(items, { alwaysTitle: true });
  } else {
    const name =
      data.refNum ||
      attrs.refNum ||
      data.description ||
      data.itemName ||
      "Manual charge";
    groups = [
      {
        en: "Particulars of services",
        ar: "تفاصيل الخدمات",
        alwaysTitle: true,
        rows: [
          {
            name: decodeName(String(name)),
            sub: decodeName(
              String(data.description || attrs.description || ""),
            ),
            qty: 1,
            price: netAmt || totalAmt,
            vat: vatAmt,
            rate: vatRate,
            saving: 0,
            discount: 0,
          },
        ],
      },
    ];
  }
  const period =
    data.periodLabel ||
    [
      formatDocDate(data.periodFrom || data.fromDate, timezone, false),
      formatDocDate(data.periodTo || data.toDate, timezone, false),
    ]
      .filter(Boolean)
      .join(" – ");

  const credit = attrs.c_creditPeriod;
  const contractNo = (
    data.contractNumber ||
    attrs.c_contractNumber ||
    ""
  )
    .toString()
    .toUpperCase();

  return {
    titleEn: "TAX INVOICE",
    titleAr: "فاتورة ضريبية",
    docWord: "invoice",
    vatRate,
    logo: brand.logo,
    badges: [{ en: "Manual invoice", ar: "فاتورة يدوية" }],
    company: {
      name: brand.name,
      nameAr: brand.nameAr,
      lines: brand.lines.length
        ? brand.lines
        : [
            company?.addressEng1,
            company?.addressEng2,
            company?.vatNumber ? `VAT: ${company.vatNumber}` : "",
            company?.phone ? `Phone: ${company.phone}` : "",
            company?.email,
          ].filter(Boolean),
      linesAr: brand.linesAr.length
        ? brand.linesAr
        : [
            company?.addressArb1,
            company?.addressArb2,
            company?.vatNumber ? `الرقم الضريبي: ${company.vatNumber}` : "",
            company?.phone ? `الهاتف: ${company.phone}` : "",
            company?.email,
          ].filter(Boolean),
    },
    notice:
      vatRate === 0 || vatAmt === 0
        ? {
            en: "No VAT charged on this invoice",
            ar: "لا تُفرض ضريبة القيمة المضافة على هذه الفاتورة",
          }
        : null,
    refs: [
      [
        "Invoice No",
        "رقم الفاتورة",
        data.invoiceNumber ||
          data.refNum ||
          (data.id != null ? `#${data.id}` : "") ||
          "",
      ],
                  [
        "Booking / Ref #",
        "رقم الحجز / المرجع",
        data.bookingId || data.serviceRefNumber || "",
      ],
          [
        "Service date",
        "تاريخ الخدمة",
        formatDocDate(data.serviceDate || data.invoiceDate, timezone, false),
      ],
      [
        "Due date",
        "تاريخ الاستحقاق",
        formatDocDate(
          attrs.invoiceDueDate || data.invoiceDueDate || data.paymentDueDate,
          timezone,
          false,
          "dateOnly",
        ),
      ],
      [
        "Credit period",
        "فترة الائتمان",
        credit != null && credit !== "" ? `${credit} days` : "",
      ],
      [
        "Reference #",
        "الرقم المرجعي",
        decodeName(String(data.refNum || attrs.refNum || "")),
      ],
      ["Contract No", "رقم العقد", contractNo],
    ],
    boxes: [
      customerBox(attrs),

      {
        en: "Contract details",
        ar: "بيانات العقد",
        name: decodeName(attrs.c_contractName || attrs.c_accountName),
        rows: [
          [
            "Contract No",
            "",
            (data.contractNumber || attrs.c_contractNumber || "")
              .toString()
              .toUpperCase(),
          ],
          ["Contract period", "", period],
          [
            "Credit period",
            "",
            credit != null && credit !== "" ? `${credit} days` : "",
          ],
        ],
      },
    ],
    table: { mode: "items", groups },
    showSavings: false,
    bank: buildBank(data.setting),
    terms: buildTermsFromSetting(data.setting),
    footer: {
      en: "This is a system generated document and needs no signatures.",
      ar: "مستند تم إنشاؤه بواسطة النظام ولا يتطلب توقيعاً.",
    },
    printed: printedNow(timezone),
  };
}

/** Tax credit note DOC (creditdebitnotes) */
export function mapCreditNoteToDoc(
  data = {},
  company,
  { timezone = "Asia/Riyadh" } = {},
) {
  const attrs = data.attributes || {};
  const brandDate = data.creditDebitDate || data.createdDate || data.invoiceDate;
  const brand = resolveCompanyBrand(company, brandDate);

  const vatAmt = +data.vatAmount || +data.saleVatAmount || +data.saleVATAmount || 0;
  const netAmt =
    +data.totalAmount || +data.saleNetAmount || +data.saleItemPrice || 0;
  const totalAmt =
    +data.netAmount ||
    (netAmt || 0) + (vatAmt || 0) ||
    +data.saleTotalAmount ||
    0;
  const vatRate =
    data.vatPercentage != null
      ? +data.vatPercentage
      : attrs.zeroVat
        ? 0
        : vatAmt > 0 && netAmt > 0
          ? Math.round((vatAmt / netAmt) * 100)
          : 5;

  const invoiceNos = (() => {
    if (Array.isArray(data.invoiceNumbers) && data.invoiceNumbers.length) {
      return data.invoiceNumbers.filter(Boolean).join(", ");
    }
    if (typeof data.invoiceNumbers === "string" && data.invoiceNumbers.trim()) {
      return data.invoiceNumbers.trim();
    }
    if (data.invoiceNumber) return String(data.invoiceNumber);
    const fromItems = (data.items || [])
      .map((it) => it.invoiceNumber)
      .filter(Boolean);
    return [...new Set(fromItems)].join(", ");
  })();

  const items = data.items || [];
  let groups;
  if (items.length) {
    groups = [
      {
        en: "Credited lines",
        ar: "البنود المرتجعة",
        alwaysTitle: true,
        rows: items.map((it) => {
          const price =
            it.saleTotalAmount != null
              ? +it.saleTotalAmount
              : it.saleItemPrice != null
                ? +it.saleItemPrice
                : +it.totalAmount || 0;
          const vat =
            it.saleTotalAmountPercent != null
              ? +it.saleTotalAmountPercent
              : it.saleVATAmount != null
                ? +it.saleVATAmount
                : it.vatAmount != null
                  ? +it.vatAmount
                  : undefined;
          const inv = it.invoiceNumber || invoiceNos || "";
          const supply = formatDocDate(
            it.supplyDate ||
              it.serviceDate ||
              it.invoiceDate ||
              data.creditDebitDate,
            timezone,
            false,
          );
          const rawName =
            it.itemName ||
            it.itemDescription ||
            it.description ||
            it.categoryName ||
            "";
          let name = decodeName(String(rawName));
          if (!name) {
            const invU = String(inv || "").toUpperCase();
            if (invU.startsWith("M-") || invU.startsWith("MI-")) {
              name = "Maintenance services · خدمات الصيانة";
            } else if (invU.startsWith("L-") || invU.startsWith("LI-")) {
              name = "Lease · إيجار";
            } else {
              name = "Credit · إشعار دائن";
            }
          }
          return {
            name,
            sub: [
              inv ? `Invoice ${inv}` : "",
              supply ? `Supply date ${supply}` : "",
            ]
              .filter(Boolean)
              .join(" · "),
            qty: it.itemQty ?? it.qty ?? 1,
            price,
            vat,
            rate: it.vatPercentage != null ? +it.vatPercentage : vatRate,
            saving: 0,
            discount: +it.discount || 0,
          };
        }),
      },
    ];
  } else {
    groups = [
      {
        en: "Credited lines",
        ar: "البنود المرتجعة",
        alwaysTitle: true,
        rows: [
          {
            name: decodeName(String(data.refNum || attrs.refNum || "Credit")),
            sub: invoiceNos ? `Invoice ${invoiceNos}` : "",
            qty: 1,
            price: netAmt || totalAmt,
            vat: vatAmt,
            rate: vatRate,
            saving: 0,
            discount: 0,
          },
        ],
      },
    ];
  }

  const contractNo = (data.contractNumber || attrs.c_contractNumber || "")
    .toString()
    .toUpperCase();
  const accountName =
    decodeName(attrs.c_accountName) ||
    decodeName(
      typeof data.receivingParty === "object"
        ? data.receivingParty?.name
        : data.receivingParty,
    ) ||
    "";
  const credit =
    attrs.c_creditPeriod != null
      ? attrs.c_creditPeriod
      : data.creditPeriod != null
        ? data.creditPeriod
        : "";
  const remaining =
    data.remainingBalance != null
      ? data.remainingBalance
      : data.invoiceBalance != null
        ? data.invoiceBalance
        : null;

  const remarks = Array.isArray(data.remarks)
    ? data.remarks.filter(Boolean).join(", ")
    : data.remarks || attrs.remarks || "";

  return {
    titleEn: "TAX CREDIT NOTE",
    titleAr: "إشعار دائن ضريبي",
    docWord: "credit note",
    vatRate,
    logo: brand.logo,
    badges: [{ en: "Credit note", ar: "إشعار دائن" }],
    company: {
      name: brand.name,
      nameAr: brand.nameAr,
      lines: brand.lines.length
        ? brand.lines
        : [
            company?.addressEng1,
            company?.addressEng2,
            company?.vatNumber ? `VAT: ${company.vatNumber}` : "",
            company?.phone ? `Phone: ${company.phone}` : "",
            company?.email,
          ].filter(Boolean),
      linesAr: brand.linesAr.length
        ? brand.linesAr
        : [
            company?.addressArb1,
            company?.addressArb2,
            company?.vatNumber ? `الرقم الضريبي: ${company.vatNumber}` : "",
            company?.phone ? `الهاتف: ${company.phone}` : "",
            company?.email,
          ].filter(Boolean),
    },
    notice: invoiceNos
      ? {
          en: `Credit against invoice ${invoiceNos}. Reduces the amount due`,
          ar: `إشعار دائن مقابل الفاتورة ${invoiceNos} ويخفض المبلغ المستحق`,
        }
      : {
          en: "This credit note reduces the amount due",
          ar: "يخفض هذا الإشعار الدائن المبلغ المستحق",
        },
    refs: [
      [
        "Credit note No",
        "رقم الإشعار الدائن",
        data.creditDebitNoteNumber || (data.id != null ? `#${data.id}` : ""),
      ],
      [
        "Credit note date",
        "تاريخ الإشعار",
        formatDocDate(data.creditDebitDate, timezone, false),
      ],
      ["Original invoice", "الفاتورة الأصلية", invoiceNos],
      ["Contract No", "رقم العقد", contractNo],
      [
        "Credit period",
        "فترة الائتمان",
        credit !== "" && credit != null ? `${credit} days` : "",
      ],
      [
        "Recorded on",
        "تاريخ التسجيل",
        formatDocDate(
          data.createdDate || data.recordedOn || data.creditDebitDate,
          timezone,
          false,
        ),
      ],
    ],
    boxes: [
      {
        ...customerBox(attrs),
        name: accountName || customerBox(attrs).name,
      },
      {
        en: "Credit details",
        ar: "تفاصيل الإشعار",
        rows: [
          ["Original invoice", "", invoiceNos],
          [
            "Reason / ref",
            "",
            decodeName(String(data.refNum || attrs.refNum || "")),
          ],
          ["Remarks", "", remarks],
          [
            "Contract",
            "",
            [accountName, contractNo].filter(Boolean).join(" · "),
          ],
          [
            "Remaining balance",
            "",
            remaining != null
              ? (+remaining).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : "",
          ],
        ],
      },
    ],
    table: { mode: "items", groups },
    totalLabels: {
      sub: ["Total returned (excl. VAT)", "المبلغ المرتجع بدون الضريبة"],
      vat: [`VAT on return ${vatRate}%`, "ضريبة المبلغ المرتجع"],
      total: ["Total credited", "إجمالي المبلغ المرتجع"],
    },
    showSavings: false,
    bank: buildBank(data.setting),
    terms: [
      {
        en: "This credit note is applied against the original invoice shown above.",
        ar: "يطبق هذا الإشعار الدائن على الفاتورة الأصلية الموضحة أعلاه.",
      },
    ],
    footer: {
      en: "This is a system generated document and needs no signatures.",
      ar: "مستند تم إنشاؤه بواسطة النظام ولا يتطلب توقيعاً.",
    },
    printed: printedNow(timezone),
  };
}

/** Tax debit note DOC (creditdebitnotes) */
export function mapDebitNoteToDoc(
  data = {},
  company,
  { timezone = "Asia/Riyadh" } = {},
) {
  const attrs = data.attributes || {};
  const brandDate = data.creditDebitDate || data.createdDate || data.invoiceDate;
  const brand = resolveCompanyBrand(company, brandDate);

  const vatAmt = +data.vatAmount || +data.saleVatAmount || +data.saleVATAmount || 0;
  const netAmt =
    +data.totalAmount || +data.saleNetAmount || +data.saleItemPrice || 0;
  const totalAmt =
    +data.netAmount ||
    (netAmt || 0) + (vatAmt || 0) ||
    +data.saleTotalAmount ||
    0;
  const vatRate =
    data.vatPercentage != null
      ? +data.vatPercentage
      : attrs.zeroVat
        ? 0
        : vatAmt > 0 && netAmt > 0
          ? Math.round((vatAmt / netAmt) * 100)
          : 5;

  const joinList = (v) => {
    if (Array.isArray(v) && v.length) return v.filter(Boolean).join(", ");
    if (typeof v === "string" && v.trim()) return v.trim();
    return "";
  };

  const paymentNos =
    joinList(data.paymentNumbers) ||
    data.paymentNumber ||
    attrs.paymentNumber ||
    "";
  const creditNos =
    joinList(data.creditNoteNumbers) ||
    data.creditNoteNumber ||
    attrs.creditNoteNumber ||
    "";

  const againstType = (() => {
    const raw = String(
      data.debitAgainst || attrs.debitAgainst || "",
    ).toLowerCase();
    if (raw.includes("credit")) return "creditNote";
    if (raw.includes("payment") || paymentNos) return "payment";
    if (creditNos) return "creditNote";
    return paymentNos ? "payment" : creditNos ? "creditNote" : "payment";
  })();

  const againstRef =
    againstType === "creditNote"
      ? creditNos || paymentNos
      : paymentNos || creditNos;
  const againstEn = againstRef
    ? againstType === "creditNote"
      ? `Credit note ${againstRef}`
      : `Payment ${againstRef}`
    : "";
  const againstAr = againstRef
    ? againstType === "creditNote"
      ? `إشعار دائن ${againstRef}`
      : `الدفعة ${againstRef}`
    : "";

  const remarks = Array.isArray(data.remarks)
    ? data.remarks.filter(Boolean).join(", ")
    : data.remarks || attrs.remarks || "";
  const refNum = decodeName(String(data.refNum || attrs.refNum || ""));

  const items = data.items || [];
  let groups;
  if (items.length) {
    groups = [
      {
        en: "Debited lines",
        ar: "البنود المدينة",
        alwaysTitle: true,
        rows: items.map((it) => {
          const price =
            it.saleTotalAmount != null
              ? +it.saleTotalAmount
              : it.saleItemPrice != null
                ? +it.saleItemPrice
                : +it.totalAmount || 0;
          const vat =
            it.saleTotalAmountPercent != null
              ? +it.saleTotalAmountPercent
              : it.saleVATAmount != null
                ? +it.saleVATAmount
                : it.vatAmount != null
                  ? +it.vatAmount
                  : undefined;
          const name =
            decodeName(
              String(
                it.itemName ||
                  it.itemDescription ||
                  it.description ||
                  remarks ||
                  "Debit",
              ),
            ) || "Debit";
          return {
            name,
            sub: [
              againstEn ? `Against ${againstEn.toLowerCase()}` : "",
              refNum ? `Ref ${refNum}` : "",
            ]
              .filter(Boolean)
              .join(" · "),
            qty: it.itemQty ?? it.qty ?? 1,
            price,
            vat,
            rate: it.vatPercentage != null ? +it.vatPercentage : vatRate,
            saving: 0,
            discount: +it.discount || 0,
          };
        }),
      },
    ];
  } else {
    groups = [
      {
        en: "Debited lines",
        ar: "البنود المدينة",
        alwaysTitle: true,
        rows: [
          {
            name: remarks || refNum || "Debit",
            sub: [
              againstEn ? `Against ${againstEn.toLowerCase()}` : "",
              refNum ? `Ref ${refNum}` : "",
            ]
              .filter(Boolean)
              .join(" · "),
            qty: 1,
            price: netAmt || totalAmt,
            vat: vatAmt,
            rate: vatRate,
            saving: 0,
            discount: 0,
          },
        ],
      },
    ];
  }

  const contractNo = (data.contractNumber || attrs.c_contractNumber || "")
    .toString()
    .toUpperCase();
  const accountName =
    decodeName(attrs.c_accountName) ||
    decodeName(
      typeof data.receivingParty === "object"
        ? data.receivingParty?.name
        : data.receivingParty,
    ) ||
    "";
  const remaining =
    data.remainingBalance != null
      ? data.remainingBalance
      : data.invoiceBalance != null
        ? data.invoiceBalance
        : null;

  return {
    titleEn: "TAX DEBIT NOTE",
    titleAr: "إشعار مدين ضريبي",
    docWord: "debit note",
    vatRate,
    logo: brand.logo,
    badges: [
      againstType === "creditNote"
        ? { en: "Against credit note", ar: "مقابل إشعار دائن" }
        : { en: "Against payment", ar: "مقابل دفعة" },
    ],
    company: {
      name: brand.name,
      nameAr: brand.nameAr,
      lines: brand.lines.length
        ? brand.lines
        : [
            company?.addressEng1,
            company?.addressEng2,
            company?.vatNumber ? `VAT: ${company.vatNumber}` : "",
            company?.phone ? `Phone: ${company.phone}` : "",
            company?.email,
          ].filter(Boolean),
      linesAr: brand.linesAr.length
        ? brand.linesAr
        : [
            company?.addressArb1,
            company?.addressArb2,
            company?.vatNumber ? `الرقم الضريبي: ${company.vatNumber}` : "",
            company?.phone ? `الهاتف: ${company.phone}` : "",
            company?.email,
          ].filter(Boolean),
    },
    notice: againstRef
      ? {
          en: `Debit against ${
            againstType === "creditNote" ? "credit note" : "payment"
          } ${againstRef}. Increases the amount due`,
          ar: `إشعار مدين مقابل ${
            againstType === "creditNote" ? "إشعار دائن" : "الدفعة"
          } ${againstRef} ويزيد المبلغ المستحق`,
        }
      : {
          en: "This debit note increases the amount due",
          ar: "يزيد هذا الإشعار المدين المبلغ المستحق",
        },
    refs: [
      [
        "Debit note No",
        "رقم الإشعار المدين",
        data.creditDebitNoteNumber || (data.id != null ? `#${data.id}` : ""),
      ],
      [
        "Debit note date",
        "تاريخ الإشعار",
        formatDocDate(data.creditDebitDate, timezone, false),
      ],
      ["Against", "مقابل", againstEn],
      ["Reference #", "الرقم المرجعي", refNum],
      ["Contract No", "رقم العقد", contractNo],
      [
        "Recorded on",
        "تاريخ التسجيل",
        formatDocDate(
          data.createdDate || data.recordedOn || data.creditDebitDate,
          timezone,
          false,
        ),
      ],
    ],
    boxes: [
      {
        ...customerBox(attrs),
        name: accountName || customerBox(attrs).name,
      },
      {
        en: "Debit details",
        ar: "تفاصيل الإشعار",
        rows: [
          ["Against", "", againstEn, againstAr],
          ["Reason / ref", "", refNum],
          ["Remarks", "", remarks],
          ["Contract No", "", contractNo],
          [
            "Remaining balance",
            "",
            remaining != null
              ? (+remaining).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : "",
          ],
        ],
      },
    ],
    table: { mode: "items", groups },
    totalLabels: {
      sub: ["Total debited (excl. VAT)", "المبلغ المدين بدون الضريبة"],
      vat: [`VAT ${vatRate}%`, "قيمة الضريبة"],
      total: ["Total debited", "إجمالي المبلغ المدين"],
    },
    showSavings: false,
    bank: buildBank(data.setting),
    terms: [
      {
        en: "This debit note increases the balance payable on the account shown above.",
        ar: "يزيد هذا الإشعار المدين الرصيد المستحق على الحساب الموضح أعلاه.",
      },
    ],
    footer: {
      en: "This is a system generated document and needs no signatures.",
      ar: "مستند تم إنشاؤه بواسطة النظام ولا يتطلب توقيعاً.",
    },
    printed: printedNow(timezone),
  };
}

/** Workshop purchase order DOC (garageinvoices / poinvoices) */
export function mapWorkshopPurchaseOrderToDoc(
  data = {},
  company,
  { timezone = "Asia/Riyadh" } = {},
) {
  const attrs = data.attributes || {};
  const brandDate =
    data.invoiceDate || data.poDate || data.createdDate || data.serviceDate;
  const brand = resolveCompanyBrand(company, brandDate);

  const items = data.items || [];
  const looksLikeDn = items.some(
    (it) => it.deliveryNoteNumber || it.dnNumber || it.dn || it.deliveryNoteId,
  );

  let groups;
  if (looksLikeDn || items.length) {
    groups = [
      {
        en: "Particulars of services",
        ar: "تفاصيل الخدمات",
        alwaysTitle: true,
        rows: items.map((it) => {
          const a = it.attributes || {};
          const dn =
            it.deliveryNoteNumber || it.dnNumber || it.dn || it.refNum || "";
          const plate = it.plateNumber || it.plate || a.plateNumber || "";
          const vehicle =
            it.vehicleName ||
            [it.make || a.make || a.v_make, it.model || a.model || a.v_model]
              .filter(Boolean)
              .join(" ") ||
            "";
          const year = it.year || a.year || a.v_year || "";
          const vehicleLabel = [vehicle, year].filter(Boolean).join(" ");
          const vin =
            it.vin || it.chassisNumber || a.vin || a.v_vin || "";
          const date = formatDocDate(
            it.serviceDate || it.deliveryNoteDate || it.createdDate,
            timezone,
            false,
          );
          const price =
            it.saleItemPrice != null
              ? +it.saleItemPrice
              : a.saleTotal != null
                ? +a.saleTotal
                : +it.saleNetAmount ||
                  +it.saleTotalAmount ||
                  +it.totalAmount ||
                  0;
          const vat =
            it.saleVATAmount != null
              ? +it.saleVATAmount
              : a.saleVat != null
                ? +a.saleVat
                : undefined;
          const name =
            decodeName(
              String(
                it.itemName ||
                  it.itemDescription ||
                  it.categoryName ||
                  "",
              ),
            ) || "Maintenance services · خدمات الصيانة";
          return {
            name,
            sub: [
              dn,
              plate,
              vehicleLabel,
              vin ? `VIN ${vin}` : "",
              date,
            ]
              .filter(Boolean)
              .join(" · "),
            qty: it.itemQty ?? it.qty ?? 1,
            price,
            vat,
            rate: it.vatPercentage,
            saving: getItemSavings(it),
            discount: +it.discount || +it.saleDiscount || 0,
          };
        }),
      },
    ];
  } else {
    groups = [
      {
        en: "Particulars of services",
        ar: "تفاصيل الخدمات",
        alwaysTitle: true,
        rows: [
          {
            name: "Maintenance services · خدمات الصيانة",
            sub: "",
            qty: 1,
            price:
              +data.saleNetAmount ||
              +data.totalAmount ||
              +attrs.estPrice ||
              0,
            vat: +data.saleVATAmount || +data.vatAmount || 0,
            // saving: 0,
            discount: 0,
          },
        ],
      },
    ];
  }

  const dnTotal =
    data.deliveryNoteCount ||
    data.totalDeliveryNotes ||
    attrs.counts ||
    attrs.deliveryNoteCount ||
    (looksLikeDn ? items.length : null);
  const dnDone =
    data.deliveryNotesDone ||
    attrs.doneCounts ||
    attrs.invoicedCount ||
    dnTotal;
  const dnLabel =
    dnTotal != null
      ? dnDone != null && dnDone !== dnTotal
        ? `${dnDone} of ${dnTotal}`
        : String(dnTotal)
      : "";

  const periodFrom = formatDocDate(
    data.periodFrom || data.fromDate || data.startDate,
    timezone,
    false,
  );
  const periodTo = formatDocDate(
    data.periodTo || data.toDate || data.endDate,
    timezone,
    false,
  );
  const period =
    data.periodLabel ||
    [periodFrom, periodTo].filter(Boolean).join(" – ") ||
    "";

  const credit =
    attrs.c_creditPeriod != null
      ? attrs.c_creditPeriod
      : data.creditPeriod != null
        ? data.creditPeriod
        : "";

  const contractNo = (
    data.contractNumber ||
    attrs.c_contractNumber ||
    attrs.contractNumber ||
    ""
  )
    .toString()
    .toUpperCase();
  const accountName =
    decodeName(attrs.c_accountName) ||
    decodeName(data.accountName) ||
    decodeName(data.contractName) ||
    "";

  const paid =
    data.paidAmount != null
      ? +data.paidAmount
      : data.paid != null
        ? +data.paid
        : null;
  const totalAmt =
    +data.saleNetAmount ||
    +data.netAmount ||
    +data.totalAmount ||
    +attrs.estPrice ||
    0;
  const balance =
    data.balance != null
      ? +data.balance
      : data.invoiceBalance != null
        ? +data.invoiceBalance
        : paid != null
          ? Math.max(0, totalAmt - paid)
          : null;

  const garageName =
    data.stationName ||
    data.garageName ||
    attrs.stationName ||
    attrs.garageName ||
    "";
  const garageNo =
    data.garageId ||
    data.stationId ||
    attrs.garageId ||
    attrs.stationId ||
    "";
  const stationCode = data.stationCode || attrs.stationCode || "";
  const area =data?.items[0]?.attributes?.garageArea;
  const city =
    data.stationCity ||
    data.city ||
    attrs.stationCity ||
    attrs.city ||
    "";
  const cityCode =
    data.cityCode || attrs.cityCode || attrs.cityShortCode || "";
  const cityLabel = city
    ? cityCode
      ? `${city} (${cityCode})`
      : city
    : cityCode || "";

  return {
    titleEn: "PURCHASE ORDER",
    titleAr: "أمر شراء",
    docWord: "purchase order",
    logo: brand.logo,
    badges: [{ en: "Workshop PO", ar: "أمر شراء ورشة" }],
    company: {
      name: brand.name,
      nameAr: brand.nameAr,
      lines: brand.lines.length
        ? brand.lines
        : [
            company?.addressEng1,
            company?.addressEng2,
            company?.vatNumber ? `VAT: ${company.vatNumber}` : "",
            company?.phone ? `Phone: ${company.phone}` : "",
            company?.email,
          ].filter(Boolean),
      linesAr: brand.linesAr.length
        ? brand.linesAr
        : [
            company?.addressArb1,
            company?.addressArb2,
            company?.vatNumber ? `الرقم الضريبي: ${company.vatNumber}` : "",
            company?.phone ? `الهاتف: ${company.phone}` : "",
            company?.email,
          ].filter(Boolean),
    },
    notice: null,
    refs: [
      [
        "PO No",
        "رقم أمر الشراء",
        data.poNumber ||
          data.invoiceNumber ||
          data.creditDebitNoteNumber ||
          (data.id != null ? `#${data.id}` : ""),
      ],
      [
        "PO date",
        "تاريخ أمر الشراء",
        formatDocDate(
          data.invoiceDate || data.poDate || data.createdDate || data.created,
          timezone,
          false,
        ),
      ],
      [
        "Due date",
        "تاريخ الاستحقاق",
        formatDocDate(
          data.attributes?.invoiceDueDate ||
            data.invoiceDueDate ||
            data.paymentDueDate,
          timezone,
          false,
          "dateOnly",
        ),
      ],
      [
        "Credit period",
        "فترة الائتمان",
        credit !== "" && credit != null ? `${credit} days` : "",
      ],
      ["Delivery notes", "مذكرات التسليم", data?.stats],
    ],
    boxes: [
      {
        en: "Garage details",
        ar: "بيانات الورشة",
        name: garageName,
        nameAr: "",
        rows: [
          ["Station code", "", stationCode != null ? String(stationCode) : ""],
          ["Area", "", area],
          ["City", "", cityLabel],
          [
            "Phone",
            "",
            data.stationPhone ||
              data.garagePhone ||
              attrs.stationPhone ||
              attrs.phone ||
              "",
          ],
          [
            "TRN",
            "",
            data.stationTaxNumber ||
              data.garageTaxNumber ||
              attrs.stationTaxNumber ||
              attrs.taxNumber ||
              "",
          ],
          [
            "Address",
            "",
            data.stationAddress ||
              data.garageAddress ||
              attrs.stationAddress ||
              attrs.address ||
              "",
          ],
        ],
      },
      {
        en: "Order details",
        ar: "تفاصيل الطلب",
        rows: [
          ["Contract No", "", contractNo],
          [
            "Fleet account",
            "",
            accountName,
            attrs.c_nameArabic || "",
          ],
          [
            "Service period",
            "",
            period ||
              [
                formatDocDate(data.serviceDateFrom || data.fromDate, timezone, false),
                formatDocDate(data.serviceDateTo || data.toDate, timezone, false),
              ]
                .filter(Boolean)
                .join(" – "),
          ],
          ["Delivery notes", "", data?.stats],
          [
            "Balance due",
            "",
            balance != null
              ? (+balance).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : "",
          ],
        ],
      },
    ],
    table: { mode: "items", groups },
    showSavings: false,
    paid: paid != null ? paid : undefined,
    bank: null,
    terms: [
      {
        en: "Invoice this purchase order by the due date, quoting the PO number and delivery note numbers.",
        ar: "يرجى إصدار الفاتورة قبل تاريخ الاستحقاق مع ذكر رقم أمر الشراء وأرقام مذكرات التسليم.",
      },
      {
        en: "Prices are as agreed under the workshop service agreement.",
        ar: "الأسعار وفق اتفاقية خدمات الورشة المعتمدة.",
      },
    ],
    footer: {
      en: "This is a system generated document and needs no signatures.",
      ar: "مستند تم إنشاؤه بواسطة النظام ولا يتطلب توقيعاً.",
    },
    printed: printedNow(timezone),
  };
}

function amountToWords(value, currencyWord = "Dirhams") {
  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const w3 = (n) => {
    let s = "";
    if (n >= 100) {
      s += `${ones[Math.floor(n / 100)]} Hundred`;
      n %= 100;
      if (n) s += " ";
    }
    if (n >= 20) {
      s += tens[Math.floor(n / 10)];
      if (n % 10) s += `-${ones[n % 10]}`;
    } else if (n) {
      s += ones[n];
    }
    return s;
  };
  let n = Math.floor(+value || 0);
  const f = Math.round(((+value || 0) - n) * 100);
  const parts = [];
  const scales = [
    [1e9, "Billion"],
    [1e6, "Million"],
    [1e3, "Thousand"],
  ];
  if (!n) parts.push("Zero");
  scales.forEach(([d, l]) => {
    if (n >= d) {
      parts.push(`${w3(Math.floor(n / d))} ${l}`);
      n %= d;
    }
  });
  if (n) parts.push(w3(n));
  return `${parts.join(" ")} ${currencyWord}${
    f ? ` and ${w3(f)} Fils` : ""
  } only`;
}

/** Workshop payment voucher DOC (garagepayments) */
export function mapWorkshopPaymentToDoc(
  data = {},
  company,
  { timezone = "Asia/Riyadh" } = {},
) {
  // API shape: { garage, payment } — also accept flat payment payloads
  const payment =
    data.payment && typeof data.payment === "object" ? data.payment : data;
  const garage =
    data.garage && typeof data.garage === "object" ? data.garage : {};
  const attrs = payment.attributes || data.attributes || {};
  const brandDate =
    payment.paymentDate ||
    payment.createdDate ||
    payment.created ||
    payment.recipientDate;
  const brand = resolveCompanyBrand(company, brandDate);

  const amount = +payment.amount || +payment.paidAmount || 0;
  const balance =
    payment.balance != null
      ? +payment.balance
      : payment.unallocatedBalance != null
        ? +payment.unallocatedBalance
        : 0;

  const garageName =
    garage.name ||
    payment.garageName ||
    payment.stationName ||
    attrs.garageName ||
    attrs.stationName ||
    "";
  const garageNo =
    garage.garageNumber != null && garage.garageNumber !== ""
      ? garage.garageNumber
      : payment.garageNumber != null && payment.garageNumber !== ""
        ? payment.garageNumber
        : garage.id != null
          ? garage.id
          : payment.garageId != null
            ? payment.garageId
            : payment.stationId != null
              ? payment.stationId
              : attrs.garageId != null
                ? attrs.garageId
                : "";

  const mode =
    payment.paymentMode || payment.mode || attrs.paymentMode || "";
  const refNum = decodeName(
    String(payment.refNum || attrs.refNum || ""),
  );
  const remarks = Array.isArray(payment.remarks)
    ? payment.remarks.filter(Boolean).join(", ")
    : payment.remarks || attrs.remarks || "";

  const appliedRaw =
    payment.invoiceNumbers ||
    payment.applied ||
    payment.poNumbers ||
    attrs.invoiceNumbers ||
    [];
  const applied = Array.isArray(appliedRaw)
    ? appliedRaw.filter(Boolean).map(String)
    : String(appliedRaw || "")
        .split(/[,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);

  const adjRaw = String(
    payment.adjustmentType || payment.adjustment || "",
  ).trim();
  const adjustment = /fifo/i.test(adjRaw)
    ? "FIFO · oldest invoices first"
    : adjRaw || "—";

  const fullyAllocated = +balance === 0 && amount > 0;
  const yesNo = (v) =>
    v === true || v === 1 || v === "1" || v === "true" ? "Yes" : "No";

  const currencyWord =
    company?.currencyWord ||
    attrs.currencyWord ||
    "Dirhams";

  const garagePhone =
    garage.phone ||
    payment.garagePhone ||
    payment.stationPhone ||
    attrs.phone ||
    attrs.garagePhone ||
    "";
  const garageTrn =
    garage.vatNo ||
    garage.taxNumber ||
    payment.garageTaxNumber ||
    payment.stationTaxNumber ||
    attrs.taxNumber ||
    attrs.garageTaxNumber ||
    "";
  const garageAddress =
    garage.address ||
    payment.garageAddress ||
    payment.stationAddress ||
    attrs.address ||
    attrs.garageAddress ||
    "";

  return {
    kind: "payment",
    titleEn: "PAYMENT TO WORKSHOP",
    titleAr: "سند صرف",
    docWord: "payment",
    direction: "out",
    logo: brand.logo,
    badges: [
      fullyAllocated
        ? { en: "Fully allocated", cls: "st-paid" }
        : { en: "Partially allocated", cls: "st-partial" },
    ],
    company: {
      name: brand.name,
      nameAr: brand.nameAr,
      lines: brand.lines.length
        ? brand.lines
        : [
            company?.addressEng1,
            company?.addressEng2,
            company?.vatNumber ? `VAT: ${company.vatNumber}` : "",
            company?.phone ? `Phone: ${company.phone}` : "",
            company?.email,
          ].filter(Boolean),
      linesAr: brand.linesAr.length
        ? brand.linesAr
        : [
            company?.addressArb1,
            company?.addressArb2,
            company?.vatNumber ? `الرقم الضريبي: ${company.vatNumber}` : "",
            company?.phone ? `الهاتف: ${company.phone}` : "",
            company?.email,
          ].filter(Boolean),
    },
    notice: null,
    refs: [
      [
        "Payment No",
        "رقم السند",
        payment.paymentNumber ||
          (payment.id != null ? `#${payment.id}` : ""),
      ],
      [
        "Payment date",
        "تاريخ الدفع",
        formatDocDate(payment.paymentDate, timezone, false),
      ],
      ["Payment mode", "طريقة الدفع", mode],
      ["Reference #", "الرقم المرجعي", refNum],
      ["Garage No", "رقم الورشة", garageNo != null ? String(garageNo) : ""],
      [
        "Recorded on",
        "تاريخ التسجيل",
        formatDocDate(
          payment.created ||
            payment.createdDate ||
            payment.recipientDate ||
            payment.paymentDate,
          timezone,
          false,
        ),
      ],
    ],
    amount,
    amountLabel: { en: "Amount paid", ar: "المبلغ المدفوع" },
    amountWords: amountToWords(amount, currencyWord),
    party: { name: garageName },
    partyLabel: { en: "Paid to", ar: "صرف إلى" },
    boxes: [
      {
        en: "Paid to (garage)",
        ar: "بيانات الورشة",
        name: garageName,
        rows: [
          ["Garage No", "", garageNo != null ? String(garageNo) : ""],
          ["Phone", "", garagePhone],
          ["TRN", "", garageTrn],
          ["Address", "", garageAddress],
        ],
      },
      {
        en: "Payment details",
        ar: "تفاصيل الدفع",
        rows: [
          ["Mode", "", mode],
          ["Reference #", "", refNum],
          [
            "Payment date",
            "",
            formatDocDate(payment.paymentDate, timezone, false),
          ],
        ],
      },
    ],
    applied,
    appliedLabel: {
      en: "Applied to purchase orders",
      ar: "أوامر الشراء المسددة",
    },
    adjustment,
    balance,
    remarks,
    terms: [],
    footer: {
      en: "This is a system generated document and needs no signatures.",
      ar: "مستند تم إنشاؤه بواسطة النظام ولا يتطلب توقيعاً.",
    },
    printed: printedNow(timezone),
  };
}

/** Customer receiving / receipt DOC (payments) — matches customer-receiving.html */
export function mapCustomerReceivingToDoc(
  data = {},
  company,
  { timezone = "Asia/Riyadh" } = {},
) {
  // API may return flat payment or { payment, account|user|customer }
  const payment =
    data.payment && typeof data.payment === "object" ? data.payment : data;
  const account =
    (data.account && typeof data.account === "object" && data.account) ||
    (data.user && typeof data.user === "object" && data.user) ||
    (data.customer && typeof data.customer === "object" && data.customer) ||
    {};
  const attrs = {
    ...(account.attributes || {}),
    ...(payment.attributes || {}),
    ...(data.attributes || {}),
  };

  const brandDate =
    payment.paymentDate ||
    payment.recipientDate ||
    payment.createdDate ||
    payment.created;
  const brand = resolveCompanyBrand(company, brandDate);

  const amount = +payment.amount || +payment.paidAmount || 0;
  const balance =
    payment.balance != null
      ? +payment.balance
      : payment.unallocatedBalance != null
        ? +payment.unallocatedBalance
        : 0;

  const partyName =
    decodeName(
      attrs.c_accountName ||
        attrs.c_contractName ||
        account.name ||
        payment.accountName ||
        payment.customerName ||
        "",
    ) || "";
  const partyNameAr = attrs.c_nameArabic || account.nameAr || "";
  const contractNo =
    payment.contractNumber ||
    attrs.c_contractNumber ||
    attrs.contractNumber ||
    account.contractNumber ||
    "";
  const contact =
    attrs.c_contactName ||
    attrs.c_contact ||
    attrs.contactName ||
    attrs.c_customerName ||
    account.contactName ||
    "";
  const email = attrs.c_email || account.email || payment.email || "";
  const mode =
    payment.paymentMode || payment.mode || attrs.paymentMode || "";
  const refNum = decodeName(
    String(payment.refNum || attrs.refNum || ""),
  );
  const remarks = Array.isArray(payment.remarks)
    ? payment.remarks.filter(Boolean).join(", ")
    : payment.remarks || attrs.remarks || "";

  const appliedRaw =
    payment.invoiceNumbers ||
    payment.applied ||
    attrs.invoiceNumbers ||
    [];
  const applied = Array.isArray(appliedRaw)
    ? appliedRaw.filter(Boolean).map(String)
    : String(appliedRaw || "")
        .split(/[,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);

  const adjRaw = String(
    payment.adjustmentType || payment.adjustment || "",
  ).trim();
  const adjustment = /fifo/i.test(adjRaw)
    ? "FIFO · oldest invoices first"
    : adjRaw || "—";

  const fullyAllocated = +balance === 0 && amount > 0;
  const yesNo = (v) =>
    v === true || v === 1 || v === "1" || v === "true" ? "Yes" : "No";

  const currencyWord =
    company?.currencyWord ||
    attrs.currencyWord ||
    "Dirhams";

  return {
    kind: "payment",
    titleEn: "CUSTOMER RECEIVING",
    titleAr: "سند قبض",
    docWord: "receiving",
    direction: "in",
    logo: brand.logo,
    company: {
      name: brand.name,
      nameAr: brand.nameAr,
      lines: brand.lines.length
        ? brand.lines
        : [
            company?.addressEng1,
            company?.addressEng2,
            company?.vatNumber ? `VAT: ${company.vatNumber}` : "",
            company?.phone ? `Phone: ${company.phone}` : "",
            company?.email,
          ].filter(Boolean),
      linesAr: brand.linesAr.length
        ? brand.linesAr
        : [
            company?.addressArb1,
            company?.addressArb2,
            company?.vatNumber ? `الرقم الضريبي: ${company.vatNumber}` : "",
            company?.phone ? `الهاتف: ${company.phone}` : "",
            company?.email,
          ].filter(Boolean),
    },
    notice: null,
    refs: [
      [
        "Receiving No",
        "رقم السند",
        payment.paymentNumber ||
          (payment.id != null ? `#${payment.id}` : ""),
      ],
      [
        "Receiving date",
        "تاريخ الاستلام",
        formatDocDate(
          payment.paymentDate || payment.recipientDate,
          timezone,
          false,
        ),
      ],
      ["Payment mode", "طريقة الدفع", mode],
      ["Reference #", "الرقم المرجعي", refNum],
      ["Contract No", "رقم العقد", contractNo],
      [
        "Recorded on",
        "تاريخ التسجيل",
        formatDocDate(
          payment.created ||
            payment.createdDate ||
            payment.recipientDate ||
            payment.paymentDate,
          timezone,
          false,
        ),
      ],
    ],
    amount,
    amountLabel: { en: "Amount received", ar: "المبلغ المستلم" },
    amountWords: amountToWords(amount, currencyWord),
    party: { name: partyName, nameAr: partyNameAr },
    partyLabel: { en: "Received from", ar: "استلمنا من" },
    boxes: [
      {
        en: "Received from",
        ar: "بيانات العميل",
        name: partyName,
        nameAr: partyNameAr,
        rows: [
          ["Contact", "", contact],
          ["Email", "", email],
          ["Contract No", "", contractNo],
        ],
      },
      {
        en: "Payment details",
        ar: "تفاصيل الدفع",
        rows: [
          ["Mode", "", mode],
          ["Reference #", "", refNum],
          [
            "Payment date",
            "",
            formatDocDate(
              payment.paymentDate || payment.recipientDate,
              timezone,
              false,
            ),
          ],
          [
            "Used credit",
            "",
            yesNo(payment.useCredit ?? payment.usedCredit ?? attrs.usedCredit),
          ],
          ["Refunded", "", yesNo(payment.refunded ?? attrs.refunded)],
        ],
      },
    ],
    applied,
    appliedLabel: {
      en: "Applied to invoices",
      ar: "الفواتير المسددة",
    },
    adjustment,
    balance,
    remarks,
    terms: [],
    thanks: {
      en: "Thank you for your payment",
      ar: "شكراً لسدادكم",
    },
    footer: {
      en: "This is a system generated document and needs no signatures.",
      ar: "مستند تم إنشاؤه بواسطة النظام ولا يتطلب توقيعاً.",
    },
    printed: printedNow(timezone),
  };
}
